/* eslint-disable max-len */
import { useCallback, useEffect, useRef, useState } from 'react';
import liaImage from '../../../../../../assets/images/lia.jpeg';
import Button from '../../../../../../components/Button';
import {
  ChatContainer,
  ChatHeader,
  ChatInputContainer,
  ChatMessages,
  InputWrapper,
  MessageBubble,
  TypingIndicator,
} from './styles';

export interface ChatMessage {
  role: 'user' | 'model' | 'assistant';
  text: string;
}

export interface AIChatProps {
  onSendMessage: (message: string) => void;
  history: ChatMessage[];
  isLoading: boolean;
  streamingText?: string;
  onStreamingDone?: () => void;
  isDifferentSubdomain: boolean;
  onClose: () => void;
}

export default function AIChat({
  onSendMessage,
  history,
  isLoading,
  streamingText = '',
  onStreamingDone,
  isDifferentSubdomain,
  onClose,
}: AIChatProps) {
  const [inputValue, setInputValue] = useState('');
  const [displayedText, setDisplayedText] = useState('');
  const pendingRef = useRef('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [history, isLoading, scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
  }, [displayedText, scrollToBottom]);

  const enqueuedLengthRef = useRef(0);

  const onStreamingDoneRef = useRef(onStreamingDone);
  useEffect(() => { onStreamingDoneRef.current = onStreamingDone; }, [onStreamingDone]);

  useEffect(() => {
    if (streamingText === '') {
      if (!intervalRef.current) {
        setDisplayedText('');
        pendingRef.current = '';
        enqueuedLengthRef.current = 0;
      }
      return;
    }
    const newChars = streamingText.slice(enqueuedLengthRef.current);
    pendingRef.current += newChars;
    enqueuedLengthRef.current += newChars.length;

    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        if (pendingRef.current.length > 0) {
          const next = pendingRef.current[0];
          pendingRef.current = pendingRef.current.slice(1);
          setDisplayedText((prev) => prev + next);
        } else {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          intervalRef.current = null;
          setDisplayedText('');
          enqueuedLengthRef.current = 0;
          onStreamingDoneRef.current?.();
        }
      }, 30);
    }
  }, [streamingText]);

  useEffect(() => () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  const handleSend = () => {
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatContainer>
      <ChatHeader>
        <div className="header-content">
          <img src={liaImage} alt="Lia" className="lia-avatar" />
          <div>
            <h3>Lia</h3>
            <span>Estou aqui para ajudar com sua solicitação</span>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
            </svg>
          </button>
        </div>
      </ChatHeader>

      <ChatMessages>
        <MessageBubble $role="assistant" $isInitial>
          <img src={liaImage} alt="Lia" className="message-avatar" />
          <div className="message-content">
            {isDifferentSubdomain
              ? 'Olá! 👋 Sou a Lia, sua assistente virtual para ajustes da roteirização.'
              : 'Olá! 👋 Sou a Lia, a assistente virtual da Capta. Estou aqui para ajudar você com sua solicitação de ajuste no vale-transporte.'}
            <br />
            <br />
            Por favor, descreva o problema que você está enfrentando com seu resultado. Quanto mais detalhes você fornecer, mais rápido conseguirei te ajudar!
            <br />
            <br />
            Antes de iniciarmos, por favor, verifique se algumas das outras opções disponibilizadas no resultado já não atendem sua necessidade!
          </div>
        </MessageBubble>

        {history
          .filter((message) => (message.role === 'user' || message.role === 'model') && !!message.text)
          .map((message) => (
            <MessageBubble key={`${message.role}-${message.text.substring(0, 20)}`} $role={message.role}>
              {message.role === 'model' ? (
                <img src={liaImage} alt="Lia" className="message-avatar" />
              ) : (
                <svg className="message-avatar user-avatar" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
                </svg>
              )}
              <div className="message-content">{message.text}</div>
            </MessageBubble>
          ))}

        {isLoading && (
          <MessageBubble $role="assistant">
            <img src={liaImage} alt="Lia" className="message-avatar" />
            {displayedText ? (
              <div className="message-content">{displayedText}</div>
            ) : (
              <TypingIndicator>
                <span />
                <span />
                <span />
              </TypingIndicator>
            )}
          </MessageBubble>
        )}

        <div ref={messagesEndRef} />
      </ChatMessages>

      <ChatInputContainer>
        <InputWrapper>
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem..."
            disabled={isLoading}
            rows={3}
          />
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            width="120px"
          >
            Enviar
          </Button>
        </InputWrapper>
      </ChatInputContainer>
    </ChatContainer>
  );
}
